import type { Location } from "@/types/order";
import { Role } from "../types/auth";

/**
 * Location utility functions for managing location sessions
 */

const LOCATION_ID_KEY = "current_location_id";
const DEVICE_TYPE_KEY = "current_device_type";

/** Virtual location assigned to orders taken outside a shop (admin, phone, web). */
export const SERVICE_CLIENT_LOCATION = "Service Client";

/** Locations that are not physical shops and must not appear in shop pickers. */
export const NON_SHOP_LOCATIONS: readonly string[] = [SERVICE_CLIENT_LOCATION, "Site"];

/** True for a physical shop location (anything not in NON_SHOP_LOCATIONS). */
export function isShopLocation(name: string): boolean {
  return !NON_SHOP_LOCATIONS.includes(name);
}

/**
 * Find location ID by location name
 * @param locationName - The location name (e.g., "Boutique A")
 * @param locations - Array of available locations
 * @returns The location ID or null if not found
 */
function findLocationIdByName(
  locationName: string,
  locations: Location[],
): number | null {
  if (!locationName) return null;

  const location = locations.find(
    (loc) => loc.name.toLowerCase() === locationName.toLowerCase(),
  );

  return location?.id ?? null;
}

/**
 * Store location ID in session storage
 * @param locationId - The location database ID
 */
function setCurrentLocationId(locationId: number): void {
  sessionStorage.setItem(LOCATION_ID_KEY, locationId.toString());
}

/**
 * Get location ID from session storage
 * @returns The location ID or null if not set
 */
export function getCurrentLocationId(): number | null {
  const storedId = sessionStorage.getItem(LOCATION_ID_KEY);
  return storedId ? parseInt(storedId, 10) : null;
}

/**
 * Clear location ID from session storage
 */
export function clearCurrentLocationId(): void {
  sessionStorage.removeItem(LOCATION_ID_KEY);
}

/**
 * Store device type in session storage
 * @param deviceType - The device type ("Caisse" or "Volant")
 */
export function setCurrentDeviceType(deviceType: "Caisse" | "Volant"): void {
  sessionStorage.setItem(DEVICE_TYPE_KEY, deviceType);
}

/**
 * Get device type from session storage
 * @returns The device type or "Caisse" as default
 */
export function getCurrentDeviceType(): "Caisse" | "Volant" {
  const storedType = sessionStorage.getItem(DEVICE_TYPE_KEY);
  return (storedType === "Volant" ? "Volant" : "Caisse") as "Caisse" | "Volant";
}

/**
 * Store location info for a user session (for Boutique/Fleuriste users)
 * @param locationName - The user's location name
 * @param locations - Array of available locations
 * @returns The matched location ID or null
 */
export async function storeUserLocationId(
  locationName: string,
  locations: Location[],
): Promise<number | null> {
  const locationId = findLocationIdByName(locationName, locations);

  if (locationId) {
    setCurrentLocationId(locationId);
    // Default to "Caisse" for Boutique/Fleuriste users if not already set
    if (!sessionStorage.getItem(DEVICE_TYPE_KEY)) {
      setCurrentDeviceType("Caisse");
    }
  }

  return locationId;
}

/** Preserve existing assignments; new shop orders belong to the authenticated shop. */
export function getOrderLocationFields(
  role: Role | null,
  userLocation: string | null,
  locations: Location[],
): { vendor_location?: string; location?: number | null } {
  if (role !== Role.BOUTIQUE && role !== Role.FLEURISTE) {
    return { vendor_location: SERVICE_CLIENT_LOCATION, location: null };
  }
  const shop = locations.find(
    (loc) => loc.name.toLowerCase() === userLocation?.trim().toLowerCase(),
  );
  if (!shop || !isShopLocation(shop.name)) {
    throw new Error(
      "Votre boutique est introuvable. Rechargez la page ou contactez un responsable.",
    );
  }
  return { vendor_location: shop.name, location: shop.id };
}
